import { useRef, useState } from 'react';
import { uploadBlob, removeMedia } from '@/lib/upload';
import { Button } from '@/components/ui/button';
import { Trash2, Upload, Loader2 } from 'lucide-react';
import { toast } from 'sonner';
import { cn } from '@/lib/utils';
import { imgUrl } from '@/lib/format';

export type AspectPreset = 'hero' | 'product' | 'product-gallery' | 'category' | 'banner' | 'square' | 'free';

interface Props {
  value: string | null | undefined;
  onChange: (url: string | null) => void | Promise<void>;
  bucket: string;
  aspect?: AspectPreset | number;
  pathPrefix?: string;
  label?: string;
  responsivePreview?: boolean;
  outputWidth?: number;
  className?: string;
  previewClassName?: string;
  /** inline = no thumbnail; just Replace button */
  inline?: boolean;
  multiple?: boolean;
  children?: React.ReactNode | ((onClick: () => void, busy: boolean) => React.ReactNode);
  /** extra classes for the upload/replace trigger button */
  buttonClassName?: string;
  /** override the trigger label (defaults to Replace/Upload) */
  buttonLabel?: string;
}

export function ImageUploader({
  value, onChange, bucket, pathPrefix = '', label,
  className, previewClassName, inline, multiple = false,
  children,
  buttonClassName, buttonLabel,
}: Props) {
  const [busy, setBusy] = useState(false);
  const [isDragging, setIsDragging] = useState(false);
  const [progress, setProgress] = useState<string | null>(null);
  const inputRef = useRef<HTMLInputElement>(null);
  const dragCounter = useRef(0);

  const pickFile = () => inputRef.current?.click();

  const handleFile = async (file: File) => {
    if (!file.type.startsWith('image/')) {
      toast.error('Please select a valid image file');
      return;
    }
    setBusy(true);
    try {
      const ext = (file.name.split('.').pop() || 'jpg').toLowerCase();
      // Pass the current value as `replace` so the old file is cleaned up server-side.
      const url = await uploadBlob(bucket, file, ext, pathPrefix, value || undefined);
      await onChange(url);
      toast.success('Image uploaded');
    } catch (e: any) {
      toast.error(e.message || 'Upload failed');
    } finally {
      setBusy(false);
    }
  };

  const handleMultipleFiles = async (files: FileList | File[]) => {
    const fileArray = Array.from(files).filter(f => f.type.startsWith('image/'));
    if (fileArray.length === 0) {
      toast.error('Please select valid image files');
      return;
    }
    setBusy(true);
    let successCount = 0;
    try {
      for (let i = 0; i < fileArray.length; i++) {
        const file = fileArray[i];
        setProgress(`Uploading ${i + 1}/${fileArray.length}...`);
        const ext = (file.name.split('.').pop() || 'jpg').toLowerCase();
        
        // When uploading multiple, do not replace the existing file.
        const url = await uploadBlob(bucket, file, ext, pathPrefix);
        await onChange(url);
        successCount++;
      }
      toast.success(`Successfully uploaded ${successCount} image(s)`);
    } catch (e: any) {
      toast.error(e.message || `Upload failed after ${successCount} successful uploads`);
    } finally {
      setBusy(false);
      setProgress(null);
    }
  };

  const handleDragEnter = (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    dragCounter.current += 1;
    if (e.dataTransfer.items && e.dataTransfer.items.length > 0) {
      setIsDragging(true);
    }
  };

  const handleDragLeave = (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    dragCounter.current -= 1;
    if (dragCounter.current <= 0) {
      dragCounter.current = 0;
      setIsDragging(false);
    }
  };

  const handleDragOver = (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
  };

  const handleDrop = (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    dragCounter.current = 0;
    setIsDragging(false);

    const files = e.dataTransfer.files;
    if (files && files.length > 0) {
      if (multiple) {
        handleMultipleFiles(files);
      } else {
        handleFile(files[0]);
      }
    }
  };

  const handleRemove = (e: React.MouseEvent) => {
    e.stopPropagation();
    const prev = value;
    onChange(null);
    if (prev) removeMedia(prev).catch(() => { /* best-effort cleanup */ });
  };

  return (
    <div className={cn('space-y-2', className)}>
      {label && <p className="text-sm font-medium">{label}</p>}
      <div
        onDragEnter={handleDragEnter}
        onDragOver={handleDragOver}
        onDragLeave={handleDragLeave}
        onDrop={handleDrop}
        className={cn(
          'relative rounded-xl transition-all duration-200',
          isDragging && 'ring-2 ring-primary ring-offset-2 bg-primary/5 rounded-xl scale-[1.01]'
        )}
      >
        {children ? (
          typeof children === 'function' ? (
            (children as Function)(pickFile, busy)
          ) : (
            <div onClick={pickFile} className="cursor-pointer">{children}</div>
          )
        ) : (
          <div className="flex flex-col sm:flex-row items-start sm:items-center gap-3">
            {value && !inline ? (
              <div
                onClick={pickFile}
                className={cn(
                  'relative group rounded-xl overflow-hidden border border-border/80 bg-muted cursor-pointer transition-all shadow-sm hover:shadow-md hover:border-primary/50',
                  previewClassName || 'w-28 h-28',
                  isDragging && 'border-primary ring-2 ring-primary/30'
                )}
              >
                <img src={imgUrl(value)} alt="" className="w-full h-full object-cover transition-transform duration-300 group-hover:scale-105" />
                <div className="absolute inset-0 bg-black/60 opacity-0 group-hover:opacity-100 transition-opacity flex flex-col items-center justify-center gap-1.5 p-2 text-white">
                  <Upload className="w-4 h-4" />
                  <span className="text-[11px] font-medium text-center">Replace image</span>
                  <Button
                    type="button"
                    size="icon"
                    variant="destructive"
                    className="h-7 w-7 mt-1 rounded-full"
                    onClick={handleRemove}
                    title="Remove"
                  >
                    <Trash2 className="w-3.5 h-3.5" />
                  </Button>
                </div>
              </div>
            ) : null}

            <div
              onClick={pickFile}
              className={cn(
                'flex-1 w-full min-h-[90px] border-2 border-dashed rounded-xl p-4 flex flex-col items-center justify-center gap-1.5 text-center cursor-pointer transition-all',
                isDragging
                  ? 'border-primary bg-primary/10 text-primary'
                  : 'border-border/70 hover:border-primary/50 hover:bg-muted/40 text-muted-foreground',
                busy && 'opacity-60 pointer-events-none'
              )}
            >
              {busy ? (
                <div className="flex flex-col items-center gap-1.5 py-1">
                  <Loader2 className="w-5 h-5 animate-spin text-primary" />
                  <span className="text-xs font-medium text-primary">
                    {progress || 'Uploading image…'}
                  </span>
                </div>
              ) : (
                <>
                  <div className="w-8 h-8 rounded-full bg-primary/10 text-primary flex items-center justify-center shrink-0">
                    <Upload className="w-4 h-4" />
                  </div>
                  <div className="space-y-0.5">
                    <p className="text-xs font-semibold text-foreground">
                      {isDragging ? 'Drop image here to upload' : buttonLabel ?? (value ? 'Click or drag to replace image' : 'Click or drag image here to upload')}
                    </p>
                    <p className="text-[11px] text-muted-foreground">
                      Supports JPG, PNG, WEBP, GIF (Max 10MB)
                    </p>
                  </div>
                </>
              )}
            </div>
          </div>
        )}

        {isDragging && (
          <div className="absolute inset-0 bg-primary/10 backdrop-blur-[1px] border-2 border-dashed border-primary rounded-xl flex items-center justify-center z-20 pointer-events-none animate-in fade-in-50 duration-150">
            <div className="bg-background/90 shadow-lg px-4 py-2 rounded-full border border-primary/40 flex items-center gap-2 text-primary text-xs font-semibold">
              <Upload className="w-4 h-4 animate-bounce" />
              <span>Drop to upload image</span>
            </div>
          </div>
        )}

        <input
          ref={inputRef}
          type="file"
          accept="image/*"
          multiple={multiple}
          className="hidden"
          onChange={(e) => {
            const files = e.target.files;
            if (files && files.length > 0) {
              if (multiple) {
                handleMultipleFiles(files);
              } else {
                handleFile(files[0]);
              }
            }
            e.target.value = '';
          }}
        />
      </div>
    </div>
  );
}
